import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Archive,
  BookOpen,
  ExternalLink,
  GripVertical,
  Loader2,
  Send,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import type { HomeworkPublicationState } from '@/lib/coaching/legacy-homework-adapter'
import { findTemplate } from '@/lib/notion-template'
import { trpc } from '@/lib/trpc'
import {
  getDifficultyColorClass,
  getDifficultyLabel,
} from '../../../lib/difficulty.js'
import type {
  CoachingHomeworkWithProblem,
  NotionTemplate,
} from '../problems/types'
import { Badge } from '../shared/badge'
import { invalidateCoachingAssignmentQueries } from './hooks/use-coaching-clients'
import { Tabs } from './tabs'

interface AssignedHomeworkTableProps {
  assignments: CoachingHomeworkWithProblem[]
  onRemove: (assignmentId: string) => void
  onRefresh?: () => void | Promise<void>
  clientId: string
  notionDatabaseId?: string | null
  notionTemplates?: NotionTemplate[] | null
  /** Default due date for individual Notion pushes (e.g. the start of the date range) */
  defaultDueDate?: string
  /** Callback when the order of active assignments changes via drag-and-drop */
  onOrderChange?: (orderedAssignments: CoachingHomeworkWithProblem[]) => void
  /** Results observed by a parent bulk operation, used to prevent unsafe replay. */
  observedDeliveryResults?: Readonly<Record<string, NotionDeliveryUiResult>>
  /** Lifts an individual result so bulk and row controls share one replay block. */
  onDeliveryResult?: (
    assignmentId: string,
    result: NotionDeliveryUiResult,
  ) => void
  /** Receives only a post-invalidation replacement snapshot. */
  onAuthoritativeDeliveryRefresh?: (
    assignments: readonly CoachingHomeworkWithProblem[],
  ) => void
}

export type NotionDeliveryState =
  | 'queued'
  | 'creating'
  | 'succeeded'
  | 'failed'
  | 'unknown'
  | 'reconciled'
  | 'archived_without_push'

interface ActivatedHomeworkFields {
  publicationState?: HomeworkPublicationState
  canPush?: boolean
}

export interface NotionDeliveryUiResult {
  /** Null only when the live route preserves a valid preexisting legacy page. */
  deliveryId: string | null
  state: NotionDeliveryState
  success: boolean
  message: string
  notionPageId: string | null
  notionUrl: string | null
}

export function canRetryNotionDelivery(
  result: NotionDeliveryUiResult | undefined,
): boolean {
  return result === undefined
}

/** Queue states that resolve in the background and should be polled. */
export function isPendingNotionDelivery(
  result: NotionDeliveryUiResult | undefined,
): boolean {
  return result?.state === 'queued' || result?.state === 'creating'
}

export function pendingNotionDeliveryHomeworkIds(
  results: Readonly<Record<string, NotionDeliveryUiResult>>,
): string[] {
  return Object.keys(results)
    .filter((homeworkId) => isPendingNotionDelivery(results[homeworkId]))
    .sort()
}

/**
 * Replaces locally observed results with the queue's authoritative snapshot.
 * Only homework the admin already interacted with is updated, so a poll can
 * never invent a replay block for untouched rows.
 */
export function mergeAuthoritativeDeliveryStates(
  current: Readonly<Record<string, NotionDeliveryUiResult>>,
  authoritative: Readonly<Record<string, NotionDeliveryUiResult>>,
): Readonly<Record<string, NotionDeliveryUiResult>> {
  let changed = false
  const next: Record<string, NotionDeliveryUiResult> = { ...current }
  for (const [homeworkId, result] of Object.entries(authoritative)) {
    const observed = next[homeworkId]
    if (observed === undefined) continue
    if (
      observed.state !== result.state ||
      observed.deliveryId !== result.deliveryId ||
      observed.notionPageId !== result.notionPageId
    ) {
      next[homeworkId] = result
      changed = true
    }
  }
  // Identity is preserved on a no-op merge so a polling caller can pass a
  // fresh snapshot object every render without re-triggering state updates.
  return changed ? next : current
}

/**
 * A locally observed result remains a replay block until a completed query
 * invalidation supplies a replacement compatibility snapshot whose explicit
 * server projection says `canPush: true`. Array identity by itself is not an
 * authority signal, so callers invoke this only at that refresh boundary.
 */
export function deliveryResultsAfterAuthoritativeRefresh(
  results: Readonly<Record<string, NotionDeliveryUiResult>>,
  assignments: readonly CoachingHomeworkWithProblem[],
): Record<string, NotionDeliveryUiResult> {
  const byId = new Map(
    assignments.map((assignment) => [assignment.id, assignment]),
  )
  return Object.fromEntries(
    Object.entries(results).filter(([assignmentId]) => {
      const assignment = byId.get(assignmentId)
      return (
        assignment === undefined ||
        activationFields(assignment).canPush !== true
      )
    }),
  )
}

function activationFields(
  assignment: CoachingHomeworkWithProblem,
): ActivatedHomeworkFields {
  return assignment as CoachingHomeworkWithProblem & ActivatedHomeworkFields
}

/** Legacy fixtures omit activation fields; live Task 11 DTOs always include them. */
export function canPushHomeworkToNotion(
  assignment: CoachingHomeworkWithProblem,
): boolean {
  const { publicationState, canPush } = activationFields(assignment)
  if (canPush !== undefined) return canPush
  return publicationState === undefined || publicationState === 'legacy'
}

export function hasHomeworkActivationMetadata(
  assignment: CoachingHomeworkWithProblem,
): boolean {
  const { publicationState, canPush } = activationFields(assignment)
  return publicationState !== undefined && canPush !== undefined
}

export function isHistoricalLegacyHomework(
  assignment: CoachingHomeworkWithProblem,
): boolean {
  const state = activationFields(assignment).publicationState
  return state === undefined || state === 'legacy'
}

function isCanonicalUnpublished(
  assignment: CoachingHomeworkWithProblem,
): boolean {
  const state = activationFields(assignment).publicationState
  return state === 'tray' || state === 'draft'
}

function isStrandedDelivery(
  result: NotionDeliveryUiResult | undefined,
): boolean {
  return result?.state === 'creating' || result?.state === 'unknown'
}

// =============================================================================
// SHARED CELL COMPONENTS
// =============================================================================

/** Renders the homework name/title with appropriate links based on type */
function HomeworkNameCell({
  assignment,
}: {
  assignment: CoachingHomeworkWithProblem
}) {
  if (assignment.type === 'PROBLEM' && assignment.problem) {
    return assignment.problem.url ? (
      <a
        href={assignment.problem.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary flex items-center gap-1.5 text-sm hover:underline"
      >
        {assignment.problem.name}
        <ExternalLink size={12} />
      </a>
    ) : (
      <span className="text-foreground text-sm">{assignment.problem.name}</span>
    )
  }

  if (assignment.type === 'SYSTEM_DESIGN' && assignment.systemDesignProblem) {
    return assignment.systemDesignProblem.url ? (
      <a
        href={assignment.systemDesignProblem.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary flex items-center gap-1.5 text-sm hover:underline"
      >
        {assignment.systemDesignProblem.title}
        <ExternalLink size={12} />
      </a>
    ) : (
      <span className="text-foreground text-sm">
        {assignment.systemDesignProblem.title}
      </span>
    )
  }

  if (assignment.type === 'SOURCE_CONTENT') {
    return (
      <div>
        <div className="flex items-center gap-1.5">
          <span className="text-foreground text-sm font-medium">
            {assignment.title ||
              assignment.sourceContent?.title ||
              'Review Task'}
          </span>
          {(assignment.url || assignment.sourceContent?.url) && (
            <a
              href={assignment.url || assignment.sourceContent?.url || ''}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center hover:underline"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
        {assignment.sourceContent?.description && (
          <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
            {assignment.sourceContent.description}
          </p>
        )}
      </div>
    )
  }

  // Default: TASK, OTHER, etc.
  return (
    <div>
      <span className="text-foreground text-sm font-medium">
        {assignment.title}
      </span>
      {assignment.url && (
        <a
          href={assignment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary ml-1.5 inline-flex items-center gap-1 hover:underline"
        >
          <ExternalLink size={12} />
        </a>
      )}
      {assignment.description && (
        <p className="text-muted-foreground mt-1 text-xs">
          {assignment.description}
        </p>
      )}
    </div>
  )
}

/** Renders the difficulty badge if present */
function HomeworkDifficultyCell({
  assignment,
}: {
  assignment: CoachingHomeworkWithProblem
}) {
  const difficulty =
    assignment.problem?.difficulty ??
    assignment.systemDesignProblem?.difficulty ??
    null
  if (!difficulty) return null
  return (
    <Badge className={getDifficultyColorClass(difficulty)}>
      {getDifficultyLabel(difficulty)}
    </Badge>
  )
}

/** Renders the source name */
function HomeworkSourceCell({
  assignment,
}: {
  assignment: CoachingHomeworkWithProblem
}) {
  return (
    <>
      {assignment.problem?.source ||
        assignment.systemDesignProblem?.source ||
        assignment.sourceContent?.sourceName ||
        '-'}
    </>
  )
}

/** Extracts the source name from a homework assignment for template matching */
function getHomeworkSourceName(
  assignment: CoachingHomeworkWithProblem,
): string | null {
  return (
    assignment.problem?.source ||
    assignment.systemDesignProblem?.source ||
    assignment.sourceContent?.sourceName ||
    null
  )
}

/** Template dropdown with auto-detect matching.
 * Override values: null = use auto-detect, "none" = explicitly no template, templateId = specific template */
function TemplateDropdown({
  assignment,
  notionTemplates,
  onTemplateChange,
  updatingTemplate,
}: {
  assignment: CoachingHomeworkWithProblem
  notionTemplates: NotionTemplate[]
  onTemplateChange: (homeworkId: string, templateId: string | null) => void
  updatingTemplate: string | null
}) {
  const autoDetectedId = findTemplate(
    notionTemplates,
    assignment.type,
    getHomeworkSourceName(assignment),
  )

  const override = assignment.notionTemplateId
  // null = no override (use auto-detect), "none" = explicitly no template, other = specific template
  const effectiveValue =
    override === null || override === undefined
      ? autoDetectedId || ''
      : override === 'none'
        ? ''
        : override

  return (
    <select
      value={effectiveValue}
      onChange={(e) => {
        e.stopPropagation()
        const selected = e.target.value
        if (selected === '') {
          // "None" selected — save "none" sentinel to explicitly disable
          onTemplateChange(assignment.id, 'none')
        } else if (selected === autoDetectedId) {
          // Selected the auto-detected template — clear override
          onTemplateChange(assignment.id, null)
        } else {
          onTemplateChange(assignment.id, selected)
        }
      }}
      onClick={(e) => e.stopPropagation()}
      disabled={updatingTemplate === assignment.id}
      className="border-border bg-background text-foreground w-full min-w-[120px] rounded border px-2 py-1 text-xs disabled:opacity-50"
      aria-label="Select Notion template"
    >
      <option value="">None</option>
      {notionTemplates.map((template) => (
        <option key={template.templateId} value={template.templateId}>
          {template.name}
        </option>
      ))}
    </select>
  )
}

/** Renders the formatted date */
function HomeworkDateCell({
  assignment,
}: {
  assignment: CoachingHomeworkWithProblem
}) {
  return <>{new Date(assignment.dateAssigned).toLocaleDateString()}</>
}

/** Renders the remove button */
function RemoveButton({
  onRemove,
  assignmentId,
}: {
  onRemove: (id: string) => void
  assignmentId: string
}) {
  return (
    <button
      type="button"
      onClick={() => onRemove(assignmentId)}
      className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded p-1.5 transition-colors"
      title="Remove from homework"
    >
      <Trash2 size={16} />
    </button>
  )
}

// =============================================================================
// ROW COMPONENTS
// =============================================================================

interface SortableRowProps {
  assignment: CoachingHomeworkWithProblem
  onRemove: (id: string) => void
  onPushToNotion: (id: string) => void
  onArchive: (id: string) => void
  pushingToNotion: string | null
  archiving: string | null
  notionDatabaseId?: string | null
  notionTemplates?: NotionTemplate[] | null
  onTemplateChange: (homeworkId: string, templateId: string | null) => void
  updatingTemplate: string | null
  deliveryResult?: NotionDeliveryUiResult
}

function SortableRow({
  assignment,
  onRemove,
  onPushToNotion,
  onArchive,
  pushingToNotion,
  archiving,
  notionDatabaseId,
  notionTemplates,
  onTemplateChange,
  updatingTemplate,
  deliveryResult,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: assignment.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  const unpublished = isCanonicalUnpublished(assignment)
  const stranded = isStrandedDelivery(deliveryResult)
  const resultBlocksPush = !canRetryNotionDelivery(deliveryResult)
  const pushEligible = canPushHomeworkToNotion(assignment)
  const needsLegacyConfiguration =
    !hasHomeworkActivationMetadata(assignment) && !notionDatabaseId
  const pushDisabled =
    needsLegacyConfiguration ||
    pushingToNotion === assignment.id ||
    !pushEligible ||
    resultBlocksPush
  const pushTitle = needsLegacyConfiguration
    ? 'Configure Notion Database ID in settings first'
    : unpublished
      ? 'Publish this assignment in Curriculum before pushing to Notion'
      : stranded
        ? 'Operator recovery is required; do not retry this delivery'
        : !pushEligible || resultBlocksPush
          ? deliveryResult?.message ||
            'This assignment is not eligible for Notion delivery'
          : 'Push to Notion'

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-border hover:bg-muted/50 border-b transition-colors cursor-grab touch-none active:cursor-grabbing ${
        isDragging ? 'bg-muted/70' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <td className="text-muted-foreground px-2 py-3">
        <GripVertical size={16} className="flex-shrink-0" />
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline">{assignment.type}</Badge>
      </td>
      <td className="px-4 py-3">
        <HomeworkNameCell assignment={assignment} />
      </td>
      <td className="px-4 py-3">
        <HomeworkDifficultyCell assignment={assignment} />
      </td>
      <td className="text-muted-foreground px-4 py-3">
        <HomeworkSourceCell assignment={assignment} />
      </td>
      <td className="text-muted-foreground px-4 py-3">
        <HomeworkDateCell assignment={assignment} />
      </td>
      <td className="px-4 py-3">
        {notionTemplates && notionTemplates.length > 0 ? (
          <TemplateDropdown
            assignment={assignment}
            notionTemplates={notionTemplates}
            onTemplateChange={onTemplateChange}
            updatingTemplate={updatingTemplate}
          />
        ) : (
          <span className="text-muted-foreground text-xs">No templates</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {deliveryResult && (
          <p
            role="status"
            aria-label="Notion delivery status"
            className={`mb-1 text-xs ${stranded ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {deliveryResult.message}
          </p>
        )}
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onArchive(assignment.id)}
            disabled={archiving === assignment.id}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
            title="Archive without pushing to Notion"
          >
            {archiving === assignment.id ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Archive size={16} />
            )}
          </button>
          <button
            type="button"
            onClick={() => onPushToNotion(assignment.id)}
            disabled={pushDisabled}
            className={`rounded p-1.5 transition-colors ${
              !pushDisabled
                ? 'text-muted-foreground hover:bg-primary/10 hover:text-primary'
                : 'cursor-not-allowed opacity-40'
            }`}
            title={pushTitle}
          >
            {pushingToNotion === assignment.id ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
          <RemoveButton onRemove={onRemove} assignmentId={assignment.id} />
        </div>
      </td>
    </tr>
  )
}

interface StaticRowProps {
  assignment: CoachingHomeworkWithProblem
  onRemove: (id: string) => void
  deliveryResult?: NotionDeliveryUiResult
}

function StaticRow({ assignment, onRemove, deliveryResult }: StaticRowProps) {
  const archivedWithoutPush =
    assignment.notionPageId === 'archived' ||
    deliveryResult?.state === 'archived_without_push'
  const remotePage =
    !archivedWithoutPush &&
    (deliveryResult?.notionUrl ??
      (assignment.notionPageId
        ? `https://www.notion.so/${assignment.notionPageId.replaceAll('-', '')}`
        : null))

  return (
    <tr className="border-border hover:bg-muted/50 border-b transition-colors">
      <td className="px-4 py-3">
        <Badge variant="outline">{assignment.type}</Badge>
      </td>
      <td className="px-4 py-3">
        <HomeworkNameCell assignment={assignment} />
      </td>
      <td className="px-4 py-3">
        <HomeworkDifficultyCell assignment={assignment} />
      </td>
      <td className="text-muted-foreground px-4 py-3">
        <HomeworkSourceCell assignment={assignment} />
      </td>
      <td className="text-muted-foreground px-4 py-3">
        <HomeworkDateCell assignment={assignment} />
      </td>
      <td className="text-muted-foreground px-4 py-3 text-xs">
        {archivedWithoutPush ? (
          'Archived without Notion page'
        ) : remotePage ? (
          <a
            href={remotePage}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-flex items-center gap-1 hover:underline"
          >
            Open Notion page
            <ExternalLink size={12} />
          </a>
        ) : (
          'Archived'
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <RemoveButton onRemove={onRemove} assignmentId={assignment.id} />
      </td>
    </tr>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function AssignedHomeworkTable({
  assignments,
  onRemove,
  onRefresh,
  clientId,
  notionDatabaseId,
  notionTemplates,
  defaultDueDate,
  onOrderChange,
  observedDeliveryResults,
  onDeliveryResult,
  onAuthoritativeDeliveryRefresh,
}: AssignedHomeworkTableProps) {
  const utils = trpc.useUtils()
  const notionPushMutation = trpc.admin.clients.notionPush.useMutation()
  const archiveHomeworkMutation =
    trpc.admin.clients.archiveHomework.useMutation()
  const updateHomeworkMutation =
    trpc.admin.clients.updateHomeworkFields.useMutation()
  const [pushingToNotion, setPushingToNotion] = useState<string | null>(null)
  const [archiving, setArchiving] = useState<string | null>(null)
  const [updatingTemplate, setUpdatingTemplate] = useState<string | null>(null)
  const [localDeliveryResults, setLocalDeliveryResults] = useState<
    Record<string, NotionDeliveryUiResult>
  >({})
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active')
  const [orderedActiveIds, setOrderedActiveIds] = useState<string[]>([])

  // Track previous order to prevent unnecessary onOrderChange calls
  const prevOrderRef = useRef<string>('')
  const assignmentsRef = useRef(assignments)
  assignmentsRef.current = assignments

  const refreshAssignmentViews = useCallback(async (): Promise<void> => {
    const previousAssignments = assignmentsRef.current
    await Promise.all([
      invalidateCoachingAssignmentQueries(utils, clientId),
      Promise.resolve(onRefresh?.()),
    ])
    const refreshedAssignments = assignmentsRef.current
    if (refreshedAssignments === previousAssignments) return
    setLocalDeliveryResults((current) =>
      deliveryResultsAfterAuthoritativeRefresh(current, refreshedAssignments),
    )
    onAuthoritativeDeliveryRefresh?.(refreshedAssignments)
  }, [clientId, onAuthoritativeDeliveryRefresh, onRefresh, utils])

  // Configure dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handlePushToNotion = async (homeworkId: string) => {
    const assignment = assignments.find(
      (candidate) => candidate.id === homeworkId,
    )
    if (
      !notionDatabaseId &&
      assignment &&
      !hasHomeworkActivationMetadata(assignment)
    ) {
      toast.error(
        'Please configure the Notion Database ID in client settings first.',
      )
      return
    }

    setPushingToNotion(homeworkId)
    try {
      const result = (await notionPushMutation.mutateAsync({
        id: clientId,
        homeworkId,
        ...(assignment && isHistoricalLegacyHomework(assignment)
          ? { dueDate: defaultDueDate }
          : {}),
      })) as NotionDeliveryUiResult

      if (onDeliveryResult) {
        onDeliveryResult(homeworkId, result)
      } else {
        setLocalDeliveryResults((current) => ({
          ...current,
          [homeworkId]: result,
        }))
      }
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      console.error('Error pushing to Notion:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to push to Notion',
      )
    } finally {
      await refreshAssignmentViews()
      setPushingToNotion(null)
    }
  }

  const handleArchive = async (homeworkId: string) => {
    setArchiving(homeworkId)
    try {
      await archiveHomeworkMutation.mutateAsync({
        id: clientId,
        homeworkId,
      })

      toast.success('Archived successfully')
    } catch (error) {
      console.error('Error archiving homework:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to archive homework',
      )
    } finally {
      await refreshAssignmentViews()
      setArchiving(null)
    }
  }

  const handleTemplateChange = useCallback(
    async (homeworkId: string, templateId: string | null) => {
      setUpdatingTemplate(homeworkId)
      try {
        await updateHomeworkMutation.mutateAsync({
          id: clientId,
          homeworkId,
          notionTemplateId: templateId,
        })

        toast.success('Template updated')
      } catch (error) {
        console.error('Error updating template:', error)
        toast.error(
          error instanceof Error ? error.message : 'Failed to update template',
        )
      } finally {
        await refreshAssignmentViews()
        setUpdatingTemplate(null)
      }
    },
    [clientId, refreshAssignmentViews, updateHomeworkMutation],
  )

  const deliveryResultFor = useCallback(
    (assignmentId: string): NotionDeliveryUiResult | undefined =>
      localDeliveryResults[assignmentId] ??
      observedDeliveryResults?.[assignmentId],
    [localDeliveryResults, observedDeliveryResults],
  )

  // Archive-without-push is terminal but deliberately has no remote page.
  // Keep the tab generic and render the two outcomes distinctly per row.
  const { baseActiveAssignments, archivedAssignments } = useMemo(() => {
    const statusActive = assignments.filter((a) => a.status === 'active')

    const isArchived = (assignment: CoachingHomeworkWithProblem) => {
      const result = deliveryResultFor(assignment.id)
      return (
        Boolean(assignment.notionPageId) ||
        result?.state === 'archived_without_push' ||
        ((result?.state === 'succeeded' || result?.state === 'reconciled') &&
          Boolean(result.notionPageId))
      )
    }

    const active = statusActive
      .filter((a) => !isArchived(a))
      .sort(
        (a, b) =>
          new Date(b.dateAssigned).getTime() -
          new Date(a.dateAssigned).getTime(),
      )

    const archived = statusActive
      .filter(isArchived)
      .sort(
        (a, b) =>
          new Date(b.dateAssigned).getTime() -
          new Date(a.dateAssigned).getTime(),
      )

    return { baseActiveAssignments: active, archivedAssignments: archived }
  }, [assignments, deliveryResultFor])

  // Initialize ordered IDs when base assignments change
  useEffect(() => {
    const baseIds = baseActiveAssignments.map((a) => a.id)
    setOrderedActiveIds((prevIds) => {
      const existingInOrder = prevIds.filter((id) => baseIds.includes(id))
      const newItems = baseIds.filter((id) => !prevIds.includes(id))
      // Only keep prevIds if nothing was added OR removed
      if (existingInOrder.length === prevIds.length && newItems.length === 0) {
        return prevIds
      }
      return [...existingInOrder, ...newItems]
    })
  }, [baseActiveAssignments])

  // Get active assignments in the user-defined order
  const activeAssignments = useMemo(() => {
    const assignmentMap = new Map(baseActiveAssignments.map((a) => [a.id, a]))
    return orderedActiveIds
      .map((id) => assignmentMap.get(id))
      .filter((a): a is CoachingHomeworkWithProblem => a !== undefined)
  }, [baseActiveAssignments, orderedActiveIds])

  // Notify parent when order changes (using ref to prevent infinite loops)
  useEffect(() => {
    const currentOrderKey = orderedActiveIds.join(',')
    if (currentOrderKey !== prevOrderRef.current) {
      prevOrderRef.current = currentOrderKey
      onOrderChange?.(activeAssignments)
    }
  }, [activeAssignments, onOrderChange, orderedActiveIds])

  // Handle drag end - reorder items
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setOrderedActiveIds((items) => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)

        // Bounds check to prevent corruption if indexOf returns -1
        if (oldIndex === -1 || newIndex === -1) {
          return items
        }

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const tabs = [
    { id: 'active', label: 'Active', count: activeAssignments.length },
    {
      id: 'archived',
      label: 'Archived',
      count: archivedAssignments.length,
    },
  ]

  if (activeAssignments.length === 0 && archivedAssignments.length === 0) {
    return (
      <div className="py-8 text-center">
        <div className="bg-muted mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full">
          <BookOpen size={24} className="text-muted-foreground" />
        </div>
        <p className="text-muted-foreground">No homework assigned yet.</p>
        <p className="text-muted-foreground text-sm">
          Use the question bank below to assign problems.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as 'active' | 'archived')}
      />

      {activeTab === 'active' && activeAssignments.length === 0 ? (
        <div className="py-8 text-center">
          <div className="bg-muted mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full">
            <BookOpen size={24} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">
            No active homework. All assignments have been archived.
          </p>
        </div>
      ) : activeTab === 'archived' && archivedAssignments.length === 0 ? (
        <div className="py-8 text-center">
          <div className="bg-muted mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full">
            <Archive size={24} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">No archived homework yet.</p>
        </div>
      ) : activeTab === 'active' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-border border-b">
                  <th className="text-muted-foreground w-10 px-2 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    <span className="sr-only">Drag handle</span>
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    Type
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    Name
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    Difficulty
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    Source
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    Date Assigned
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                    Template
                  </th>
                  <th className="text-muted-foreground px-4 py-3 text-right text-xs font-medium tracking-wider uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={orderedActiveIds}
                  strategy={verticalListSortingStrategy}
                >
                  {activeAssignments.map((assignment) => (
                    <SortableRow
                      key={assignment.id}
                      assignment={assignment}
                      onRemove={onRemove}
                      onPushToNotion={handlePushToNotion}
                      onArchive={handleArchive}
                      pushingToNotion={pushingToNotion}
                      archiving={archiving}
                      notionDatabaseId={notionDatabaseId}
                      notionTemplates={notionTemplates}
                      onTemplateChange={handleTemplateChange}
                      updatingTemplate={updatingTemplate}
                      deliveryResult={deliveryResultFor(assignment.id)}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </div>
        </DndContext>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-border border-b">
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Type
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Name
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Difficulty
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Source
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Date Assigned
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Delivery
                </th>
                <th className="text-muted-foreground px-4 py-3 text-right text-xs font-medium tracking-wider uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {archivedAssignments.map((assignment) => (
                <StaticRow
                  key={assignment.id}
                  assignment={assignment}
                  onRemove={onRemove}
                  deliveryResult={deliveryResultFor(assignment.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
